import React, { useState, useEffect } from 'react';
import { 
  Inbox, Phone, Mail, Clock, Calendar, CheckCircle2, 
  AlertCircle, Trash2, RefreshCw, Filter, ExternalLink,
  User, MessageSquare, Check, Home, Zap, Key, Eye, EyeOff, Send, ShieldCheck, ChevronDown, ChevronUp
} from 'lucide-react';
import { 
  getInquiriesFromFirebase, 
  updateInquiryStatusInFirebase, 
  deleteInquiryFromFirebase, 
  InquiryData 
} from '../../services/firebaseService';

export default function InquiriesManager() {
  const [inquiries, setInquiries] = useState<InquiryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'in_progress' | 'completed'>('all');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Resend Email Settings State
  const [emailConfig, setEmailConfig] = useState({
    apiKey: '',
    toEmail: 'imotencentervarna1@gmail.com',
    fromEmail: 'Варна Имотен Център <onboarding@resend.dev>',
    hasApiKey: false,
    apiKeyMasked: '',
    isConfigured: false
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<{ success: boolean; message: string } | null>(null);
  const [showEmailConfig, setShowEmailConfig] = useState(true);

  const fetchEmailSettings = async () => {
    try {
      const res = await fetch('/api/email-settings');
      if (res.ok) {
        const data = await res.json();
        setEmailConfig(prev => ({
          ...prev,
          toEmail: data.toEmail || 'imotencentervarna1@gmail.com',
          fromEmail: data.fromEmail || 'Варна Имотен Център <onboarding@resend.dev>',
          hasApiKey: data.hasApiKey,
          apiKeyMasked: data.apiKeyMasked,
          isConfigured: data.isConfigured
        }));
      }
    } catch (e) {
      console.error('Failed to load email settings:', e);
    }
  };

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const data = await getInquiriesFromFirebase();
      setInquiries(data);
    } catch (err: any) {
      console.error('Error fetching inquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
    fetchEmailSettings();
  }, []);

  const handleSaveEmailSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingEmail(true);
    setEmailFeedback(null);
    try {
      const payload: any = {
        toEmail: emailConfig.toEmail.trim(),
        fromEmail: emailConfig.fromEmail.trim()
      };
      if (emailConfig.apiKey.trim()) {
        payload.apiKey = emailConfig.apiKey.trim();
      }

      const res = await fetch('/api/email-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setEmailFeedback({ success: true, message: 'Имейл настройките бяха запазени успешно!' });
        setEmailConfig(prev => ({
          ...prev,
          apiKey: '',
          hasApiKey: data.hasApiKey,
          apiKeyMasked: data.hasApiKey ? '••••••••' : '',
          isConfigured: data.hasApiKey && Boolean(prev.toEmail)
        }));
      } else {
        setEmailFeedback({ success: false, message: data.error || 'Грешка при запазване.' });
      }
    } catch (err: any) {
      setEmailFeedback({ success: false, message: err.message || 'Мрежова грешка.' });
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setIsTestingEmail(true);
    setEmailFeedback(null);
    try {
      const res = await fetch('/api/email-settings/test', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setEmailFeedback({ success: true, message: data.message });
      } else {
        setEmailFeedback({ success: false, message: data.message });
      }
    } catch (err: any) {
      setEmailFeedback({ success: false, message: `Грешка при тестване: ${err.message}` });
    } finally {
      setIsTestingEmail(false);
    }
  };

  const handleUpdateStatus = async (id: string, newStatus: 'new' | 'in_progress' | 'completed') => {
    try {
      setActionLoadingId(id);
      await updateInquiryStatusInFirebase(id, newStatus);
      setInquiries(prev => prev.map(item => item.id === id ? { ...item, status: newStatus } : item));
      setFeedback('Статусът беше обновен успешно.');
      setTimeout(() => setFeedback(null), 3000);
    } catch (err: any) {
      alert(`Грешка при обновяване: ${err.message}`);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleResendEmail = async (inq: InquiryData) => {
    if (!inq.id) return;
    setActionLoadingId(inq.id);
    try {
      const res = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: inq.name,
          phone: inq.phone,
          email: inq.email,
          propertyId: inq.propertyId,
          propertyTitle: inq.propertyTitle,
          message: inq.message,
          source: inq.type === 'consultation' ? 'consultation_modal' : inq.type === 'inspection' ? 'property_modal' : 'contact_page'
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await updateInquiryStatusInFirebase(inq.id, inq.status, { emailStatus: 'sent', emailError: '' });
        setInquiries(prev => prev.map(item => item.id === inq.id ? { ...item, emailStatus: 'sent', emailError: '' } : item));
        setFeedback('Имейлът беше изпратен успешно до брокера!');
      } else {
        const errMsg = data.error || data.message || 'Грешка при изпращане';
        await updateInquiryStatusInFirebase(inq.id, inq.status, { emailStatus: 'failed', emailError: errMsg });
        setInquiries(prev => prev.map(item => item.id === inq.id ? { ...item, emailStatus: 'failed', emailError: errMsg } : item));
        alert(`Грешка при изпращане на имейл: ${errMsg}`);
      }
    } catch (err: any) {
      alert(`Грешка при изпращане на имейл: ${err.message}`);
    } finally {
      setActionLoadingId(null);
      setTimeout(() => setFeedback(null), 3500);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете това запитване?')) return;
    try {
      setActionLoadingId(id);
      await deleteInquiryFromFirebase(id);
      setInquiries(prev => prev.filter(item => item.id !== id));
      setFeedback('Запитването беше изтрито.');
      setTimeout(() => setFeedback(null), 3000);
    } catch (err: any) {
      alert(`Грешка при изтриване: ${err.message}`);
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredInquiries = inquiries.filter(item => {
    if (statusFilter === 'all') return true;
    return item.status === statusFilter;
  });

  const newCount = inquiries.filter(i => i.status === 'new').length;
  const inProgressCount = inquiries.filter(i => i.status === 'in_progress').length;
  const completedCount = inquiries.filter(i => i.status === 'completed').length;

  return (
    <div className="space-y-6 font-sans text-slate-800">
      {/* Top Banner & Stats */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Inbox className="w-5 h-5 text-[#C5A46E]" />
            <h2 className="text-xl font-bold text-[#0F1E32]">Входящи запитвания и заявки за оглед</h2>
            {newCount > 0 && (
              <span className="bg-amber-100 text-amber-800 text-xs px-2.5 py-0.5 rounded-full font-bold">
                {newCount} нови
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Всички съобщения, заявки за оглед и запитвания за консултация от сайта се съхраняват сигурно в облачната база данни.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEmailConfig(!showEmailConfig)}
            className={`px-3.5 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer border ${
              emailConfig.hasApiKey 
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100' 
                : 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100'
            }`}
          >
            <Mail className="w-3.5 h-3.5 text-[#C5A46E]" />
            <span>Имейл настройки (Resend)</span>
            {showEmailConfig ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          <button
            onClick={fetchInquiries}
            disabled={loading}
            className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Обнови
          </button>
        </div>
      </div>

      {/* Resend Email Configuration Card */}
      {showEmailConfig && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 animate-fade-in space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center font-black">
                <Send className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-[#0F1E32]">Настройки за автоматични имейл известия (Resend API)</h3>
                <p className="text-[11px] text-slate-500">
                  Всяко ново запитване за оглед или безплатна консултация се изпраща моментално по пощата.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {emailConfig.hasApiKey ? (
                <span className="px-3 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full text-xs font-bold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  🟢 Resend е свързан
                </span>
              ) : (
                <span className="px-3 py-1 bg-amber-50 text-amber-800 border border-amber-300 rounded-full text-xs font-bold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  🟡 Липсва API Key (въведете re_...)
                </span>
              )}
            </div>
          </div>

          {emailFeedback && (
            <div className={`p-3 rounded-xl text-xs font-semibold flex items-start gap-2 border ${
              emailFeedback.success 
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900' 
                : 'bg-rose-50 border-rose-200 text-rose-900'
            }`}>
              {emailFeedback.success ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <span>{emailFeedback.message}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSaveEmailSettings} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Field 1: Resend API Key */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5 text-[#C5A46E]" />
                    Resend API Key:
                  </span>
                  {emailConfig.hasApiKey && (
                    <span className="text-[10px] text-emerald-600 font-bold lowercase">
                      активен ({emailConfig.apiKeyMasked})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    placeholder={emailConfig.hasApiKey ? 'Зададен (въведете нов за промяна)' : 're_xxxxxxxxxxxxxxxxxxxxxx'}
                    value={emailConfig.apiKey}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <span className="text-[10px] text-slate-400 block mt-1">
                  Вземете вашия ключ от <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#C5A46E] hover:underline font-bold">resend.com/api-keys</a>.
                </span>
              </div>

              {/* Field 2: Notification Recipient Email (To) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-[#C5A46E]" />
                  Получател на известията (To):
                </label>
                <input
                  type="email"
                  required
                  placeholder="imotencentervarna1@gmail.com"
                  value={emailConfig.toEmail}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, toEmail: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                />
                <span className="text-[10px] text-slate-400 block mt-1">
                  Имейлът на брокерската фирма, на който да пристигат запитванията.
                </span>
              </div>

              {/* Field 3: Sender (From) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5 text-[#C5A46E]" />
                  Подател (From):
                </label>
                <input
                  type="text"
                  required
                  placeholder="Варна Имотен Център <onboarding@resend.dev>"
                  value={emailConfig.fromEmail}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, fromEmail: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                />
                <span className="text-[10px] text-slate-400 block mt-1">
                  По подразбиране: <code className="text-slate-600 font-mono">onboarding@resend.dev</code> или ваш домейн.
                </span>
              </div>
            </div>

            {/* Hint Box regarding Resend domain deliverability */}
            <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-900 flex flex-col gap-1.5">
              <div className="flex items-start gap-2">
                <span className="text-sm">💡</span>
                <div className="leading-relaxed">
                  <strong>Важно за доставка в безплатния план на Resend:</strong> Ако изпращачът е <code className="font-mono bg-white px-1 py-0.5 rounded border border-amber-200">onboarding@resend.dev</code>, Resend разрешава изпращане само до имейла, с който сте регистрирани в resend.com. За да изпращате до произволна поща, добавете и потвърдете Вашия фирмен домейн в <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline font-bold text-amber-950">resend.com/domains</a>.
                </div>
              </div>
              <div className="flex items-start gap-2 pt-1 border-t border-amber-200/60">
                <span className="text-sm">📩</span>
                <div className="leading-relaxed text-amber-950">
                  <strong>Проверка в Gmail:</strong> Ако не намирате писмото във „Входящи“, задължително проверете папка <strong>„Спам“ (Spam / Junk)</strong> на <code className="font-mono bg-white px-1 rounded">{emailConfig.toEmail}</code> и натиснете <em>„Не е спам“</em>.
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 pt-1 flex-wrap">
              <button
                type="submit"
                disabled={isSavingEmail}
                className="px-4 py-2.5 bg-[#0F1E32] hover:bg-[#1A3353] text-white text-xs font-bold rounded-xl shadow transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <ShieldCheck className="w-4 h-4 text-[#C5A46E]" />
                <span>{isSavingEmail ? 'Запазване...' : 'Запази имейл настройките'}</span>
              </button>

              <button
                type="button"
                onClick={handleTestEmail}
                disabled={isTestingEmail || !emailConfig.hasApiKey}
                className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl shadow transition flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Zap className={`w-4 h-4 ${isTestingEmail ? 'animate-spin' : ''}`} />
                <span>{isTestingEmail ? 'Изпращане на тест...' : '⚡ Изпрати тестов имейл'}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
            statusFilter === 'all'
              ? 'bg-[#0F1E32] text-white shadow'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          Всички ({inquiries.length})
        </button>
        <button
          onClick={() => setStatusFilter('new')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
            statusFilter === 'new'
              ? 'bg-amber-600 text-white shadow'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-amber-400"></span>
          Нови ({newCount})
        </button>
        <button
          onClick={() => setStatusFilter('in_progress')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
            statusFilter === 'in_progress'
              ? 'bg-blue-600 text-white shadow'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-blue-400"></span>
          В процес ({inProgressCount})
        </button>
        <button
          onClick={() => setStatusFilter('completed')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
            statusFilter === 'completed'
              ? 'bg-emerald-600 text-white shadow'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
          Обработени ({completedCount})
        </button>
      </div>

      {/* Inquiries List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm">
          <RefreshCw className="w-6 h-6 animate-spin text-[#C5A46E] mx-auto mb-2" />
          <p className="text-xs text-slate-500 font-semibold">Зареждане на запитванията от облачната база данни...</p>
        </div>
      ) : filteredInquiries.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm">
          <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-bold text-slate-700">Няма намерени запитвания</p>
          <p className="text-xs text-slate-400 mt-1">
            {statusFilter === 'all' 
              ? 'Все още няма получени съобщения или запитвания от формулярите на сайта.'
              : `Няма запитвания с избран статус "${statusFilter}".`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredInquiries.map((inq) => {
            const isNew = inq.status === 'new';
            const isInProgress = inq.status === 'in_progress';
            const isCompleted = inq.status === 'completed';

            const formattedDate = inq.createdAt 
              ? new Date(inq.createdAt).toLocaleString('bg-BG', { 
                  dateStyle: 'medium', 
                  timeStyle: 'short' 
                })
              : '—';

            return (
              <div 
                key={inq.id}
                className={`bg-white rounded-2xl p-5 border transition shadow-sm ${
                  isNew 
                    ? 'border-amber-300 ring-2 ring-amber-100 bg-amber-50/20' 
                    : isInProgress
                    ? 'border-blue-200 bg-blue-50/10'
                    : 'border-slate-200 opacity-90'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    {/* Header info */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-base text-[#0F1E32] flex items-center gap-1.5">
                        <User className="w-4 h-4 text-[#C5A46E]" />
                        {inq.name}
                      </span>
                      
                      <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                        isNew 
                          ? 'bg-amber-100 text-amber-800' 
                          : isInProgress
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {isNew ? 'Ново запитване' : isInProgress ? 'В обработка' : 'Обработено'}
                      </span>

                      <span className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-medium">
                        {inq.type === 'inspection' ? 'Заявка за оглед' : inq.type === 'consultation' ? 'Консултация' : 'Общо запитване'}
                      </span>

                      {inq.emailStatus === 'sent' && (
                        <span className="text-[11px] bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <Check className="w-3 h-3 text-emerald-600" />
                          Изпратен по имейл
                        </span>
                      )}
                      {inq.emailStatus === 'failed' && (
                        <span className="text-[11px] bg-rose-50 text-rose-800 border border-rose-200 px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1" title={inq.emailError || ''}>
                          <AlertCircle className="w-3 h-3 text-rose-600" />
                          Неизпратен по имейл {inq.emailError ? `(${inq.emailError})` : ''}
                        </span>
                      )}

                      <span className="text-[11px] text-slate-400 flex items-center gap-1 ml-auto">
                        <Clock className="w-3 h-3" />
                        {formattedDate}
                      </span>
                    </div>

                    {/* Contact details */}
                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      <a 
                        href={`tel:${inq.phone.replace(/\s+/g, '')}`}
                        className="font-bold text-slate-700 hover:text-blue-600 flex items-center gap-1.5 bg-slate-50 hover:bg-blue-50 px-2.5 py-1 rounded-lg border border-slate-200 transition"
                      >
                        <Phone className="w-3.5 h-3.5 text-blue-600" />
                        {inq.phone}
                      </a>

                      {inq.email && (
                        <a 
                          href={`mailto:${inq.email}`}
                          className="text-slate-600 hover:text-blue-600 flex items-center gap-1.5 bg-slate-50 hover:bg-blue-50 px-2.5 py-1 rounded-lg border border-slate-200 transition"
                        >
                          <Mail className="w-3.5 h-3.5 text-amber-600" />
                          {inq.email}
                        </a>
                      )}

                      {inq.propertyTitle && (
                        <div className="text-slate-700 flex items-center gap-1.5 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                          <Home className="w-3.5 h-3.5 text-[#C5A46E]" />
                          <span className="font-semibold">Имот:</span> {inq.propertyTitle} {inq.propertyId ? `(#${inq.propertyId})` : ''}
                        </div>
                      )}
                    </div>

                    {/* Message body */}
                    {inq.message && (
                      <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-xs text-slate-700 leading-relaxed mt-2 flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                        <div>{inq.message}</div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex md:flex-col items-center md:items-end gap-2 shrink-0 border-t md:border-t-0 pt-3 md:pt-0">
                    <div className="flex flex-wrap items-center gap-1">
                      <button
                        onClick={() => handleResendEmail(inq)}
                        disabled={actionLoadingId === inq.id}
                        title="Изпрати или препрати известието до брокера по имейл"
                        className="px-3 py-1.5 bg-amber-50 hover:bg-[#C5A46E] hover:text-[#0F1E32] text-amber-900 border border-amber-200 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Изпрати имейл</span>
                      </button>

                      {isNew && (
                        <button
                          onClick={() => inq.id && handleUpdateStatus(inq.id, 'in_progress')}
                          disabled={actionLoadingId === inq.id}
                          className="px-3 py-1.5 bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-700 border border-blue-200 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                        >
                          <Clock className="w-3.5 h-3.5" />
                          Поеми
                        </button>
                      )}

                      {!isCompleted && (
                        <button
                          onClick={() => inq.id && handleUpdateStatus(inq.id, 'completed')}
                          disabled={actionLoadingId === inq.id}
                          className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-700 border border-emerald-200 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Приключи
                        </button>
                      )}

                      {isCompleted && (
                        <button
                          onClick={() => inq.id && handleUpdateStatus(inq.id, 'new')}
                          disabled={actionLoadingId === inq.id}
                          className="px-3 py-1.5 bg-slate-50 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded-lg text-xs font-medium transition cursor-pointer"
                        >
                          Маркирай ново
                        </button>
                      )}

                      <button
                        onClick={() => inq.id && handleDelete(inq.id)}
                        disabled={actionLoadingId === inq.id}
                        title="Изтрий запитването"
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
